import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'
import type { DayRange } from '@krp/ui-kit'

import type { ShiftStateFilter, ShiftsIntentState, ShiftsView } from './types'

/**
 * Намерение человека в разделе «Смены» по каждому кабинету: период, место, кассир, состояние и «только мои». Смен здесь НЕТ —
 * они в кэше RTK Query. Срок жизни — экран: вкладка помнит отбор через свой снимок (capture/restore окна).
 */
export const EMPTY_SHIFTS_VIEW: ShiftsView = { from: '', to: '', place: '', cashier: '', state: '', mineOnly: false, collapsed: [] }

/** Сколько кабинетов и свёрнутых узлов помнит намерение: брошенное не копится. */
export const MAX_REMEMBERED_RESOURCES = 8
export const MAX_COLLAPSED_NODES = 200

const initialState: ShiftsIntentState = { byResource: {} }

function viewOf(state: ShiftsIntentState, resourceId: string): ShiftsView {
  return state.byResource[resourceId] ?? EMPTY_SHIFTS_VIEW
}

function remember(state: ShiftsIntentState, resourceId: string, view: ShiftsView) {
  state.byResource[resourceId] = view
  const ids = Object.keys(state.byResource)
  for (const id of ids.slice(0, Math.max(0, ids.length - MAX_REMEMBERED_RESOURCES))) {
    if (id !== resourceId) delete state.byResource[id]
  }
}

const shiftsSlice = createSlice({
  name: 'aqsi/shifts',
  initialState,
  reducers: {
    shiftsPeriodChosen(state, action: PayloadAction<{ resourceId: string; range: DayRange }>) {
      const { resourceId, range } = action.payload
      remember(state, resourceId, { ...viewOf(state, resourceId), from: range.from, to: range.to })
    },
    /** Место сменилось — кассир прежнего места может там не работать: отбор по кассиру снимается. */
    shiftsPlaceChosen(state, action: PayloadAction<{ resourceId: string; place: string }>) {
      const { resourceId, place } = action.payload
      remember(state, resourceId, { ...viewOf(state, resourceId), place, cashier: '' })
    },
    shiftsNodeToggled(state, action: PayloadAction<{ resourceId: string; nodeId: string }>) {
      const { resourceId, nodeId } = action.payload
      const view = viewOf(state, resourceId)
      const collapsed = view.collapsed.includes(nodeId)
        ? view.collapsed.filter((id) => id !== nodeId)
        : [...view.collapsed, nodeId].slice(-MAX_COLLAPSED_NODES)
      remember(state, resourceId, { ...view, collapsed })
    },
    shiftsCashierChosen(state, action: PayloadAction<{ resourceId: string; cashier: string }>) {
      const { resourceId, cashier } = action.payload
      remember(state, resourceId, { ...viewOf(state, resourceId), cashier })
    },
    shiftsStateChosen(state, action: PayloadAction<{ resourceId: string; state: ShiftStateFilter }>) {
      const { resourceId } = action.payload
      remember(state, resourceId, { ...viewOf(state, resourceId), state: action.payload.state })
    },
    shiftsMineOnlyChosen(state, action: PayloadAction<{ resourceId: string; mineOnly: boolean }>) {
      const { resourceId, mineOnly } = action.payload
      remember(state, resourceId, { ...viewOf(state, resourceId), mineOnly })
    },
    shiftsViewRestored(state, action: PayloadAction<{ resourceId: string; view: ShiftsView }>) {
      remember(state, action.payload.resourceId, action.payload.view)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      delete state.byResource[action.payload.resourceId]
    })
  },
})

export const {
  shiftsPeriodChosen,
  shiftsPlaceChosen,
  shiftsNodeToggled,
  shiftsCashierChosen,
  shiftsStateChosen,
  shiftsMineOnlyChosen,
  shiftsViewRestored,
} = shiftsSlice.actions
export const shiftsReducer = shiftsSlice.reducer

export function selectShiftsView(state: ShiftsIntentState, resourceId: string): ShiftsView {
  return viewOf(state, resourceId)
}

const DATE = /^\d{4}-\d{2}-\d{2}$/
const MAX_TEXT = 200

const dateField = (value: unknown): string | undefined =>
  typeof value === 'string' && (value === '' || DATE.test(value)) ? value : undefined
const textField = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length <= MAX_TEXT ? value : undefined

/** Проверка снимка вкладки: битое — `undefined`, вкладка открывается с отбором по умолчанию. */
export function decodeShiftsView(raw: unknown): ShiftsView | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const value = raw as Record<string, unknown>
  const from = dateField(value.from)
  const to = dateField(value.to)
  const place = textField(value.place)
  const cashier = textField(value.cashier)
  const state = value.state
  const mineOnly = value.mineOnly
  const collapsed = value.collapsed
  if (from === undefined || to === undefined || place === undefined || cashier === undefined) return undefined
  if (state !== '' && state !== 'open' && state !== 'closed') return undefined
  if (typeof mineOnly !== 'boolean') return undefined
  if (
    !Array.isArray(collapsed) ||
    collapsed.length > MAX_COLLAPSED_NODES ||
    !collapsed.every((id): id is string => typeof id === 'string' && id !== '' && id.length <= MAX_TEXT + 20)
  ) {
    return undefined
  }
  return { from, to, place, cashier, state, mineOnly, collapsed }
}
